/**
 * RecordManualPaymentDialog — Sales/Admin/Finance log a payment received
 * OUTSIDE the Stripe link (wire, check, Zelle…). Runs the same post-payment
 * pipeline server-side: clears production, creates commitments, notifies
 * Finance + PM + Sales.
 */
import { useEffect, useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const PAYMENT_METHODS = [
  { value: "wire",        label: "Wire Transfer" },
  { value: "ach",         label: "ACH" },
  { value: "check",       label: "Check" },
  { value: "zelle",       label: "Zelle" },
  { value: "credit_card", label: "Credit Card (offline)" },
  { value: "cash",        label: "Cash" },
  { value: "other",       label: "Other" },
];

export function RecordManualPaymentDialog({ co, paymentType = "deposit", onClose, onSaved }) {
  const [method, setMethod] = useState("wire");
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const isBalance = paymentType === "balance";

  useEffect(() => {
    if (!co) return;
    const defaultAmount = isBalance
      ? (co.balance_amount || 0)
      : (co.deposit_amount || co.final_client_price || co.total_amount || 0);
    setAmount(defaultAmount ? String(defaultAmount) : "");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setMethod("wire");
    setNote("");
  }, [co, isBalance]);

  const submit = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/change-orders/${co.id}/record-manual-payment`, {
        payment_type: paymentType,
        payment_method: method,
        amount: parseFloat(amount) || null,
        payment_date: paymentDate || null,
        note: note.trim() || null,
      });
      toast.success(isBalance ? "Balance payment recorded" : "Payment recorded — cleared for production");
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to record payment";
      toast.error(typeof msg === "string" ? msg : "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!co} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="co-manual-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Manual Payment</DialogTitle>
          <DialogDescription>
            Log a {isBalance ? "balance" : ""} payment made outside the Stripe link for{" "}
            <span className="font-medium text-slate-700">{co?.short_id}</span>
            {co?.project_name ? <> — {co.project_name}</> : null}.
            The usual paid notifications (Finance, PM, Sales) will fire.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-slate-600">Amount (USD)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="manual-payment-amount"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-slate-600">Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                data-testid="manual-payment-date"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-slate-600">Payment Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="manual-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-slate-600">Note <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              placeholder="e.g. Wire ref #48211, received via Chase"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="manual-payment-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !parseFloat(amount)} data-testid="manual-payment-submit">
            {saving ? "Recording…" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
